import { useCallback } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import type { TableRow } from "./DataTable";
import { RequiredCells, RequiredValues, requiredCellValues } from "./cellValue";

const labelOf = <IDType>(row: TableRow<IDType>, name: string) =>
  row.values.find(cell => cell.name === name)?.label ?? name;

export function useRequiredCells() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const sayRequired = useCallback((labels: string[]) =>
    labels.forEach(field => enqueueSnackbar(t("validation.fieldRequired", { field }), { variant: "error", autoHideDuration: 3000 })),
  [enqueueSnackbar, t]);

  const readRequired = useCallback(<IDType, const S extends RequiredCells>(row: TableRow<IDType>, cells: S): RequiredValues<S> | null => {
    const required = requiredCellValues(row, cells);
    if (required.ok) return required.values;
    sayRequired(required.missing.map(name => labelOf(row, name)));
    return null;
  }, [sayRequired]);

  /** For values a save has already read, each beside the label the user sees. A page that keeps 0 or '' for "not chosen" maps it to null first. */
  const requireFilled = useCallback((values: readonly (readonly [label: string, value: unknown])[]) => {
    const missing = values.filter(([, value]) => value == null).map(([label]) => label);
    sayRequired(missing);
    return missing.length === 0;
  }, [sayRequired]);

  const saveWhenFilled = useCallback(<V, IDType = number>(label: string, save: (value: V, row: TableRow<IDType>) => unknown) =>
    async (row: TableRow<IDType>, value: V | null | undefined) => {
      if (value == null) {
        sayRequired([labelOf(row, label)]);
        return;
      }
      await save(value, row);
    }, [sayRequired]);

  return { readRequired, requireFilled, saveWhenFilled };
}
